'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';

const Footer = () => {
  const [currentYear, setCurrentYear] = useState<number | null>(null);

  useEffect(() => {
    setCurrentYear(new Date()?.getFullYear());
  }, []);

  const footerLinks = {
    company: [
      { label: 'About Diaax', href: '/about' },
      { label: 'Our Approach', href: '/our-approach' },
      { label: 'Flagship Program', href: '/flagship-program' }
    ],
    services: [
      { label: 'Strategy & Institutional Design', href: '/services' },
      { label: 'Leadership & Capacity Building', href: '/services' },
      { label: 'Change & Execution Enablement', href: '/services' }
    ],
    contact: [
      { label: 'Request Conversation', href: '/contact' },
      { label: 'Email: info@diaax.com', href: 'mailto:info@diaax.com' },
      { label: 'WhatsApp: +447888397822', href: 'https://wa.me/447888397822', isWhatsApp: true },
      { label: '128, City Road, London, EC1V 2NX, UNITED KINGDOM', href: '#' }
    ]
  };

  return (
    <footer 
      className="relative text-white overflow-hidden"
      style={{
        backgroundImage: 'linear-gradient(rgba(7, 15, 28, 0.78), rgba(7, 15, 28, 0.78)), url(/assets/images/image-1766156304424.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Decorative accent line at top */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-teal-500/30 to-transparent" />
      
      <div className="container mx-auto px-4 py-12 lg:py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-12">
          {/* Brand Section */}
          <div>
            <Link 
              href="/homepage" 
              className="inline-block mb-4 group"
              aria-label="Diaax Homepage"
            >
              <h2 className="text-3xl font-bold text-white tracking-tight leading-none group-hover:text-teal-400 transition-colors duration-300">
                Diaax
              </h2>
            </Link>
            <p className="text-sm text-white/70 leading-relaxed">
              Strategy & Capacity Building for Institutional Transformation
            </p>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="font-semibold mb-4 text-white text-base">Company</h3>
            <ul className="space-y-2.5">
              {footerLinks?.company?.map((link) => (
                <li key={link?.href}>
                  <Link
                    href={link?.href}
                    className="text-sm text-white/70 hover:text-teal-400 transition-colors duration-300 inline-flex items-center group"
                  >
                    <span className="relative">
                      {link?.label}
                      <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-teal-400 group-hover:w-full transition-all duration-300" />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h3 className="font-semibold mb-4 text-white text-base">Services</h3>
            <ul className="space-y-2.5">
              {footerLinks?.services?.map((link, index) => (
                <li key={index}>
                  <Link
                    href={link?.href}
                    className="text-sm text-white/70 hover:text-teal-400 transition-colors duration-300 inline-flex items-center group"
                  >
                    <span className="relative">
                      {link?.label}
                      <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-teal-400 group-hover:w-full transition-all duration-300" />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Links */}
          <div>
            <h3 className="font-semibold mb-4 text-white text-base">Contact</h3>
            <ul className="space-y-2.5">
              {footerLinks?.contact?.map((link, index) => (
                <li key={index}>
                  <Link
                    href={link?.href}
                    target={link?.isWhatsApp ? '_blank' : undefined}
                    rel={link?.isWhatsApp ? 'noopener noreferrer' : undefined}
                    className={`text-sm text-white/70 hover:text-teal-400 transition-all duration-300 inline-flex items-center group ${
                      link?.isWhatsApp ? 'hover:scale-110 hover:translate-x-1' : ''
                    }`}
                  >
                    {link?.isWhatsApp && (
                      <span className="mr-2 text-lg animate-pulse">💬</span>
                    )}
                    <span className="relative">
                      {link?.label}
                      <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-teal-400 group-hover:w-full transition-all duration-300" />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar with gradient separator */}
        <div className="relative">
          <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          
          <div className="pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-sm text-white/60 font-light">
                &copy; {currentYear || '2025'} Diaax Ltd. All rights reserved.
              </p>
              <div className="flex items-center space-x-6">
                <Link 
                  href="/privacy-policy" 
                  className="text-sm text-white/60 hover:text-teal-400 transition-colors duration-300 font-light"
                >
                  Privacy Policy
                </Link>
                <span className="text-white/20">|</span>
                <Link 
                  href="/terms-of-service" 
                  className="text-sm text-white/60 hover:text-teal-400 transition-colors duration-300 font-light"
                >
                  Terms of Service
                </Link>
                <span className="text-white/20">|</span>
                <Link
                  href="https://www.linkedin.com/company/diaax-ltd/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-white/60 hover:text-teal-400 transition-colors duration-300 font-light inline-flex items-center gap-1.5"
                  aria-label="Diaax on LinkedIn"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  LinkedIn
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;